import { NgClass } from "@angular/common";
import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, computed, signal } from "@angular/core";
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { AuthPermIdentifier } from "@myapp/modules/fwcore/models";
import { RequestUserPermission, ResponseUserPermission } from "@myapp/modules/fwcore/models/messages";
import { AuthorizationService } from "@myapp/modules/fwcore/services";
import { IsgHelperService } from "@mysrc/services/framework";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AuthTreeComponent } from "../../components/auth/auth-tree/auth-tree.component";
import { ModulesListComponent } from "../../components/modules/modules-list/modules-list.component";
import { RolesListComponent } from "../../components/roles/roles-list/roles-list.component";
import { UserListComponent } from "../../components/users/user-list/user-list.component";
import { ICompany, Module, RequestLicense, Role } from "../../models";
import { AdminService } from "../../services";
import { IsgSizerDirective } from "../../../prob/directives/isg-sizer.directive";

@Component({
  selector: 'app-page-license-roles',
  standalone: true,
  templateUrl: './page-license-roles.component.html',
  styleUrls: ['./page-license-roles.component.scss'],
  imports: [RouterLink, ModulesListComponent, RolesListComponent, AuthTreeComponent, UserListComponent, IsgSizerDirective]
})
export class PageLicenseRolesComponent implements OnInit, OnDestroy, AfterViewInit {


  //------------------------------ Constants -----------------------------

  protected readonly ctCodeRoleSave = 'role-set';
  protected readonly ctCodeRoleDetail = 'role-detail';
  protected readonly ctCodeRoleDelete = 'role-delete';
  protected readonly ctCodeRoleTemplate = 'role-template-set';

  protected readonly ctCodePermCreate = 'perm-create';
  protected readonly ctCodePermEdit = 'perm-edit';
  protected readonly ctCodePermDetail = 'perm-detail';
  protected readonly ctCodePermReOrder = 'perm-reorder';
  protected readonly ctCodePermDelete = 'perm-delete';

  /*-------------------------- public properties / methods --------------------*/

  public currentLicenseId: string | null = null;

  public currentModuleId!: string;

  public currentRoleId: string | undefined | null = null;

  public companyName: string | undefined | null = null;

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(ModulesListComponent)
  protected moduleListComponent!: ModulesListComponent;

  @ViewChild(RolesListComponent)
  protected rolesListComponent!: RolesListComponent;

  @ViewChild('authtreevisual')
  protected authTreeVisualComponent!: AuthTreeComponent;

  @ViewChild('authtreedata')
  protected authTreeDataComponent!: AuthTreeComponent;

  @ViewChild(UserListComponent)
  protected userListComponent!: UserListComponent;

  /*-------------------------- State  --------------------------*/

  protected companies: ICompany[] | undefined;

  protected destroy$ = new Subject();

  protected afterViewInit: boolean = false;

  protected saveRolePermissions: boolean | null = null;

  protected hideModule: boolean = false;

  protected hideAuthDataTree: boolean = false;

  //-------------------- Permission Properties -------------------------

  /** Permission request payload for the current route. */
  protected reqPerm: RequestUserPermission = {

  }

  /** Permissions returned for the current user/route. */
  protected perms?: AuthPermIdentifier[] = [];

  /** Permission codes resolved for the current user/route. */
  protected permCodes = signal<Set<string>>(new Set<string>());

  /** True once the user-permissions request has completed. */
  protected permsLoaded = false;

  protected hasRoleSave = computed<boolean>(() => this.permCodes().has(this.ctCodeRoleSave));
  protected hasRoleDetail = computed<boolean>(() => this.permCodes().has(this.ctCodeRoleDetail));
  protected hasRoleDelete = computed<boolean>(() => this.permCodes().has(this.ctCodeRoleDelete));
  protected hasRoleTemplate = computed<boolean>(() => this.permCodes().has(this.ctCodeRoleTemplate));

  protected hasPermCreate = computed<boolean>(() => this.permCodes().has(this.ctCodePermCreate));
  protected hasPermEdit = computed<boolean>(() => this.permCodes().has(this.ctCodePermEdit));
  protected hasPermDetail = computed<boolean>(() => this.permCodes().has(this.ctCodePermDetail));
  protected hasPermReOrder = computed<boolean>(() => this.permCodes().has(this.ctCodePermReOrder));
  protected hasPermDelete = computed<boolean>(() => this.permCodes().has(this.ctCodePermDelete));

  /*-------------------------- Constructor  --------------------------*/

  constructor(protected adminService: AdminService
    , protected serviceAuth: AuthorizationService
    , protected activatedRoute: ActivatedRoute
    , protected router: Router
    , protected hlp: IsgHelperService) {

  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit() {

    this.activatedRoute.queryParamMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (params) => {
          // check the input id value before assignment to avoid injection
          this.currentLicenseId = this.hlp.getValidGUID(params.get('id'));

          if (!this.currentLicenseId) { this.router.navigate(['./admin/license']); }

          this.updateCompanyInfo();

        });

    this.adminService.getAllCompanies().pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => { this.companies = resp; }
    );


    this.reqPerm.hierarchyCodes = ['roles', 'permissions'];

    this.loadUserPermissions(this.reqPerm);

  }

  ngAfterViewInit(): void {

    this.afterViewInit = true;
    this.moduleListComponent.Initialized();
  }

  ngOnDestroy() {

    this.destroy$.next();
    this.destroy$.complete();

  }

  /** Fetches user permissions for the current route and applies derived flags. */
  protected loadUserPermissions(reqPerm: RequestUserPermission): void {

    this.serviceAuth.getData$<RequestUserPermission, ResponseUserPermission>(reqPerm, this.serviceAuth.endpointUserPermission)
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {

        this.perms = resp.data ?? [];

        this.permCodes.set(new Set(this.perms.map(p => p.code).filter((c): c is string => !!c)));

        this.permsLoaded = true;
      });
  }

  /*--------------------------- event handlers  --------------------------*/

  protected onModuleSelected(ev: Module | null) {
    if (ev?.id) {
      this.currentModuleId = ev.id;
    }
  }

  protected onModulesLoaded(count: number | null) {
    let v = !!(count) ? (count < 2) : true;
    this.hideModule = v;
  }

  protected onRoleSelected(role: Role | null) {

    if (role?.id)
      this.userListComponent.Initialized();

    this.currentRoleId = role?.id;
  }

  protected onSaveRolePermissionsClicked(isSave: boolean) {
    if (isSave) {
      this.saveRolePermissions = isSave;
    }
  }

  protected onDataTreeLoaded(count: number | null) {
    let v = !!(count) ? (count < 1) : true;
    this.hideAuthDataTree = v;
  }

  protected onNavigateToLicense(): void {
    this.router.navigate(['/admin/license'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  protected onNavigateToRole(): void {
    this.router.navigate(['/admin/role'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  protected onNavigateToUserManagement(): void {
    this.router.navigate(['/admin/user/license'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  protected onNavigateToUserPermission(): void {
    this.router.navigate(['/admin/user/permission'], {
      queryParams: { id: this.currentLicenseId },
    });
  }

  /*--------------------------- internal functions  --------------------------*/

  protected updateCompanyInfo() {

    let req: RequestLicense = { id: this.currentLicenseId! };

    this.adminService.getLicense$(req).pipe(takeUntil(this.destroy$)).subscribe(
      (resp) => {
        if (resp.data && resp.data.length === 1)
          this.companyName = resp.data[0].companyName;
        else
          this.companyName = "";
      }
    );
  }

}



